import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { useConsentTracking, replayQueuedConsents } from '@/hooks/useConsentTracking';
import { useProfileCompletion } from '@/hooks/useProfileCompletion';
import { DOCUMENT_VERSIONS } from '@/lib/constants/documentVersions';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import SEOHead from '@/components/common/SEOHead';
import { SITE_URL } from '@/lib/constants/urls';
import AuthForm from '@/components/auth/AuthForm';
import ProfileCompletionModal from '@/components/auth/ProfileCompletionModal';
import ResetPasswordForm from '@/components/auth/ResetPasswordForm';

const Auth = () => {
  const [redirectMessage, setRedirectMessage] = useState('');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [searchParams] = useSearchParams();
  const isSignUp = searchParams.get('signup') === 'true';
  const isResetMode = searchParams.get('mode') === 'reset';
  
  const { user } = useAuth();
  const { logConsent, getClientInfo } = useConsentTracking();
  const { checkProfile } = useProfileCompletion();
  const navigate = useNavigate();

  useEffect(() => {
    // Check for redirect message from sessionStorage
    const message = sessionStorage.getItem('authMessage');
    if (message) {
      setRedirectMessage(message);
      sessionStorage.removeItem('authMessage');
    }
  }, []);

  useEffect(() => {
    // Skip if no user yet
    if (!user) return;

    // Skip redirect if we're in password reset mode — user needs to stay on this page
    if (isResetMode) return;

    let cancelled = false;

    // Handle post-Google OAuth consent logging and profile check
    const handlePostAuth = async () => {
      // Handle Google OAuth consent logging — log split rows (terms + privacy)
      // matching the email signup flow. Cookie consent is collected separately
      // via the cookie banner, not at signup.
      const pendingConsent = sessionStorage.getItem('pending_consent_google');
      if (pendingConsent) {
        try {
          const consentData = JSON.parse(pendingConsent);
          const clientInfo = getClientInfo();
          const termsVersion = consentData.termsVersion || DOCUMENT_VERSIONS.termsOfService;
          const privacyVersion = consentData.privacyVersion || DOCUMENT_VERSIONS.privacyPolicy;
          await Promise.all([
            logConsent({
              consentType: 'terms_acceptance',
              documentVersion: termsVersion,
              userAgent: clientInfo.userAgent,
            }, user.id),
            logConsent({
              consentType: 'privacy_policy_acceptance',
              documentVersion: privacyVersion,
              userAgent: clientInfo.userAgent,
            }, user.id),
          ]);

          // Seed acknowledged-versions baseline so the policy-update banner
          // does not fire for a brand-new Google signup.
          try {
            const map = JSON.parse(localStorage.getItem('acknowledgedVersions') || '{}');
            map.termsOfService = termsVersion;
            map.privacyPolicy = privacyVersion;
            map.cookiePolicy = DOCUMENT_VERSIONS.cookiePolicy;
            localStorage.setItem('acknowledgedVersions', JSON.stringify(map));
          } catch {
            // ignore localStorage failures
          }

          sessionStorage.removeItem('pending_consent_google');
        } catch (error) {
          console.error('Error logging Google OAuth consent:', error);
        }
      }

      // Drain any consent rows that failed to persist earlier (e.g. queued
      // during signup before the session existed). Best-effort, silent.
      replayQueuedConsents(user.id).catch(() => {});

      if (cancelled) return;

      // Check if profile needs completion (especially for Google users)
      const needsCompletion = await checkProfile();
      
      if (cancelled) return;

      if (needsCompletion) {
        setShowProfileModal(true);
      } else {
        // Redirect authenticated users with complete profiles
        // Check URL params first, then sessionStorage
        const redirectPath = searchParams.get('return_to') || searchParams.get('redirect') || sessionStorage.getItem('redirectAfterAuth') || '/';
        sessionStorage.removeItem('redirectAfterAuth');
        navigate(redirectPath);
      }
    };

    handlePostAuth();

    return () => {
      cancelled = true;
    };
  }, [user?.id, isResetMode]); // Also depend on isResetMode

  const handleProfileComplete = () => {
    setShowProfileModal(false);
    const redirectPath = sessionStorage.getItem('redirectAfterAuth') || '/';
    sessionStorage.removeItem('redirectAfterAuth');
    navigate(redirectPath);
  };

  const handleProfileSkip = () => {
    setShowProfileModal(false);
    const redirectPath = sessionStorage.getItem('redirectAfterAuth') || '/';
    sessionStorage.removeItem('redirectAfterAuth');
    navigate(redirectPath);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead 
        title={isResetMode ? "Reset Password | PaperVerse" : isSignUp ? "Sign Up | PaperVerse" : "Sign In | PaperVerse"}
        description={isResetMode
          ? "Set a new password for your PaperVerse account."
          : isSignUp 
            ? "Create your PaperVerse account to upload papers, request reviews, and collaborate with researchers worldwide."
            : "Sign in to PaperVerse to access your papers, reviews, and research collaboration tools."
        }
        canonicalUrl={`${SITE_URL}/auth`}
        keywords={isSignUp 
          ? "sign up, researcher account, academic registration, create account, join research community"
          : "sign in, login, researcher account, academic login, access papers"
        }
        noindex
      />
      <Navbar />
      
      <main className="flex-1 flex items-center justify-center px-4 pt-24 pb-16">
        {isResetMode ? (
          <div className="w-full max-w-md">
            <ResetPasswordForm />
          </div>
        ) : (
          <Card className="w-full max-w-md">
            <CardHeader className="space-y-1">
              <div className="flex items-center gap-2 mb-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(-1)}
                  className="gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
              </div>
              
              {redirectMessage && (
                <Alert className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{redirectMessage}</AlertDescription>
                </Alert>
              )}
              
              <CardTitle className="text-2xl text-center">
                Welcome to PaperVerse
              </CardTitle>
              <CardDescription className="text-center">
                Sign in or create an account to continue
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <AuthForm 
                defaultMode={isSignUp ? 'signup' : 'signin'}
                showForgotPassword={true}
              />
            </CardContent>
          </Card>
        )}
      </main>
      
      <Footer />

      <ProfileCompletionModal
        open={showProfileModal}
        onOpenChange={setShowProfileModal}
        onComplete={handleProfileComplete}
        onSkip={handleProfileSkip}
      />
    </div>
  );
};

export default Auth;
